@if (isProduction) {
  <mat-card>
    <mat-card-content>
      Demo-seeder er ikke tilgængelig i produktion (kun test/staging).
    </mat-card-content>
  </mat-card>
} @else if (!allowed) {
  <mat-card>
    <mat-card-content>Kun superadmin har adgang til demo-seeder.</mat-card-content>
  </mat-card>
} @else {
  <mat-card class="cprng-seeder-card">
    <mat-card-header>
      <mat-card-title>CPRNG demo-seeder (org 11)</mat-card-title>
      <mat-card-subtitle>
        Opretter fiktive brugere, moduler, vedligeholdelse, events og deltagelse — kun test.
        Path-version: <strong>{{ context?.path_version_name ?? 'V1' }}</strong>
      </mat-card-subtitle>
    </mat-card-header>
    <mat-card-content>
      @if (loading) {
        <mat-progress-bar mode="indeterminate" />
      }
      @if (errorMessage) {
        <p class="error">{{ errorMessage }}</p>
      }

      <div class="warn-banner">
        <strong>Kun organisation ID 11.</strong> Lokationer og uddannelsesveje (V1) skal findes
        på forhånd. Kør seed rydder automatisk tidligere demo-seed data (ikke lokationer/veje).
        API kræver test-miljø: <code>IS_TESTING = true</code> (eller local <code>IS_DEV</code>) i RCMS
        <code>config.php</code>.
        @if (context && !context.enabled) {
          <div class="error">API kører som produktion (IS_TESTING/IS_DEV er false) — run/cleanup blokeres.</div>
        }
      </div>

      @if (context?.preflight?.blocking?.length) {
        <p class="error">
          Preflight: {{ context!.preflight!.blocking!.join(' ') }}
        </p>
      }
      @if (context?.preflight?.warnings?.length) {
        <p class="hint">{{ context!.preflight!.warnings!.join(' ') }}</p>
      }

      <p class="hint">
        Org: {{ context?.org_name ?? '…' }} (id {{ context?.organizations_id ?? 11 }}) ·
        {{ context?.locations?.length ?? 0 }} lokationer ·
        {{ context?.paths?.length ?? 0 }} paths i DB ·
        <strong>{{ context?.seed_scope_paths?.length ?? seedScopeCount() }} i seed-scope</strong>
      </p>
      @if (seedScopeNames().length) {
        <p class="hint">
          Seed-scope (path_users, vedligehold, path-events):
          <strong>{{ seedScopeNames().join(', ') }}</strong>
        </p>
      }

      <h3>Brugere pr. lokation</h3>
      <div class="grid-2">
        <mat-form-field appearance="outline">
          <mat-label>Min. brugere</mat-label>
          <input matInput type="number" [(ngModel)]="config.usersPerLocationMin" />
        </mat-form-field>
        <mat-form-field appearance="outline">
          <mat-label>Maks. brugere</mat-label>
          <input matInput type="number" [(ngModel)]="config.usersPerLocationMax" />
        </mat-form-field>
      </div>

      <h3>Events</h3>
      <div class="grid-2">
        <mat-form-field appearance="outline">
          <mat-label>Maks. antal events</mat-label>
          <input matInput type="number" [(ngModel)]="config.totalEventsCap" />
        </mat-form-field>
        <mat-form-field appearance="outline">
          <mat-label>Random seed (valgfri)</mat-label>
          <input matInput type="number" [(ngModel)]="config.randomSeed" />
        </mat-form-field>
      </div>
      <div>
        <mat-checkbox [(ngModel)]="eventTypeExercise">Øvelser</mat-checkbox>
        <mat-checkbox [(ngModel)]="eventTypeEducation">Uddannelser</mat-checkbox>
        <mat-checkbox [(ngModel)]="eventTypeCourse">Kurser</mat-checkbox>
      </div>
      <p class="hint">
        Preview: <code>events_estimate</code> = uddannelser (org-wide) + øvelser (én event pr.
        vedligeholdelsesmodul pr. lokation). Kun «Uddannelser» giver curriculum-events (FUI/ISL/HL).
      </p>

      <h3>Øvelser — gennemførelse %</h3>
      <div class="grid-2">
        <mat-form-field appearance="outline">
          <mat-label>Min. %</mat-label>
          <input matInput type="number" [(ngModel)]="config.exerciseCompletionPercentMin" />
        </mat-form-field>
        <mat-form-field appearance="outline">
          <mat-label>Maks. %</mat-label>
          <input matInput type="number" [(ngModel)]="config.exerciseCompletionPercentMax" />
        </mat-form-field>
      </div>

      <h3>Deltagelse — tilmelding % pr. event</h3>
      <div class="grid-2">
        <mat-form-field appearance="outline">
          <mat-label>Min. %</mat-label>
          <input matInput type="number" [(ngModel)]="config.eventEnrollmentPercentMin" />
        </mat-form-field>
        <mat-form-field appearance="outline">
          <mat-label>Maks. %</mat-label>
          <input matInput type="number" [(ngModel)]="config.eventEnrollmentPercentMax" />
        </mat-form-field>
      </div>

      <h3>Uddannelsesveje</h3>
      <button mat-stroked-button type="button" (click)="applyPathPresets()">
        Preset FUI / HL+HL TL / ISL
      </button>
      @if (config.pathAllocationRules?.length) {
        <ul>
          @for (rule of config.pathAllocationRules; track $index) {
            <li>
              {{ rule.userPercent }}%
              @if (rule.grouped) {
                (gruppe)
              }
              —
              @if (rule.pathGuids?.length) {
                @for (g of rule.pathGuids; track g) {
                  <span class="path-row">{{ pathLabel(g!) }}</span>
                }
              } @else {
                {{ rule.pathNameContains }}
              }
            </li>
          }
        </ul>
      }

      @if (context?.last_run) {
        <h3>Seneste kørsel</h3>
        @if (context!.last_run!.status === 'running') {
          <p class="error">
            Kørsel ser ud til at hænge (status running). Den nulstilles automatisk efter ~2 min ved
            genindlæsning, eller brug knappen herunder.
          </p>
        }
        <pre class="stats-pre">{{ context!.last_run | json }}</pre>
      }

      <div class="actions">
        <button mat-stroked-button type="button" (click)="preview()" [disabled]="loading">
          Preview
        </button>
        <button mat-flat-button color="primary" type="button" (click)="runSeed()" [disabled]="loading">
          Kør seed
        </button>
        <button mat-stroked-button color="warn" type="button" (click)="cleanup()" [disabled]="loading">
          Ryd seed
        </button>
        <button mat-stroked-button type="button" (click)="resetRunning()" [disabled]="loading">
          Nulstil hængende kørsel
        </button>
      </div>

      @if (previewResult) {
        <h3>Preview</h3>
        <pre class="stats-pre">{{ previewResult }}</pre>
      }
      @if (runResult) {
        <h3>{{ errorMessage ? 'Fejl / svar' : 'Resultat' }}</h3>
        <pre class="stats-pre">{{ runResult }}</pre>
      }
    </mat-card-content>
  </mat-card>
}

<!-- CMS: opret RCMS-side med selector app-duna-cprng-seeder (superadmin, kun test) -->
